import { baseApi } from '@alltrails/redux-helpers/baseApi';
import type {
  AlgoliaHit,
  AreaSearchResult,
  FilterSearchResult,
  GuideSearchResult,
  ListSearchResult,
  LocationType,
  PlaceSearchResult,
  POISearchResult,
  SponsoredListSearchResult,
  TrailSearchResult
} from '@alltrails/types/AlgoliaResults';
import type { LocaleUnits } from '@alltrails/types/LocaleUnits';

export type SuggestionsParams = {
  limit?: number;
  localeUnits?: LocaleUnits;
  query: string;
  recordTypesToReturn?: (TrailSearchResult['type'] | Omit<LocationType, 'photospot'> | 'filter' | 'guide' | 'list' | 'sponsored_list')[];
};

export type SuggestionsResponse = {
  searchResults: (
    | AreaSearchResult
    | FilterSearchResult
    | GuideSearchResult
    | ListSearchResult
    | PlaceSearchResult
    | POISearchResult
    | SponsoredListSearchResult
    | TrailSearchResult
  )[];
  summary: {
    count: number;
  };
};

export type RecommendedSuggestionsParams = {
  limit?: number;
  localeUnits?: LocaleUnits;
  location?: { lat: number; lng: number };
};

export type RecommendedSuggestionsResponse = {
  meta: {
    title: string;
    kind: string;
  };
  results: AlgoliaHit[];
};

export const suggestionsApi = baseApi.enhanceEndpoints({ addTagTypes: ['Suggestions', 'RecommendedSuggestions'] }).injectEndpoints({
  endpoints: builder => ({
    /* https://github.com/alltrails/explore-service/blob/alpha/api/paths/suggestions.yaml */
    getSuggestions: builder.query<SuggestionsResponse, SuggestionsParams>({
      query: ({ limit, localeUnits, query, recordTypesToReturn }) => {
        return {
          url: `/api/alltrails/explore/v1/suggestions`,
          data: {
            query,
            limit,
            ...(recordTypesToReturn ? { recordTypesToReturn } : {})
          },
          method: 'post',
          ...(localeUnits && { config: { headers: { 'X-Locale-Units': localeUnits } } })
        };
      }
    }),
    /* https://github.com/alltrails/explore-service/blob/alpha/api/paths/recommended_suggestions.yaml */
    getRecommendedSuggestions: builder.query<RecommendedSuggestionsResponse, RecommendedSuggestionsParams | void>({
      query: ({ limit = 5, localeUnits, location } = {}) => ({
        url: `/api/alltrails/explore/v1/recommended_suggestions`,
        method: 'get',
        config: {
          params: {
            limit,
            ...(location ? { 'location[center][lat]': location.lat, 'location[center][lng]': location.lng } : {})
          },
          ...(localeUnits && { headers: { 'X-Locale-Units': localeUnits } })
        }
      }),
      providesTags: ['RecommendedSuggestions']
    })
  })
});

export const { useLazyGetSuggestionsQuery, useGetRecommendedSuggestionsQuery, useLazyGetRecommendedSuggestionsQuery } = suggestionsApi;
